/* eslint-disable import/no-extraneous-dependencies */

import { GridColDef } from '@mui/x-data-grid';
import { BrowseView, browseViewColumnAction } from '@pro4-erp/core-web';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';

import { getBeautifiedLocalDate } from '@/core';
import { AuditorVisit } from '../../types';
import { getAuditorVisitsForMeOpened } from '../../api/getAuditorVisitsForMeOpened';

const OpenedBrowseView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const columns: GridColDef<AuditorVisit>[] = [
    {
      ...browseViewColumnAction({
        field: 'actions',
        headerName: t('Open', { defaultValue: 'Open' }),
        icon: {
          element: <EditIcon />,
          color: 'primary',
        },
        onClick: ({ params }) => navigate(`/auditor-visits/${params.id}`),
      }),
      display: 'flex',
    },
    {
      field: 'workOrder',
      display: 'flex',
      headerName: t('Work order', { defaultValue: 'Work order' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      sortable: false,
      valueGetter(_, row) {
        return row.workOrder?.name || 'N/A';
      },
    },
    {
      field: 'proposedTime',
      display: 'flex',
      headerName: t('Proposed time', { defaultValue: 'Proposed time' }).toString(),
      flex: 1.5,
      minWidth: 50,
      maxWidth: 70,
      disableColumnMenu: true,
    },
    {
      field: 'planedTime',
      display: 'flex',
      headerName: t('Planed time', { defaultValue: 'Planed time' }).toString(),
      flex: 1.5,
      minWidth: 50,
      maxWidth: 70,
      disableColumnMenu: true,
    },
    {
      field: 'services',
      display: 'flex',
      headerName: t('Services', { defaultValue: 'Services' }).toString(),
      flex: 1.5,
      minWidth: 250,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (params.row.services) {
          return (
            <List>
              {params.row.services.map((service) => (
                <ListItem key={service.id}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                    primary={service.serviceShowAs}
                    secondary={
                      <Box>
                        <Typography fontSize="0.8rem">
                          {t('Standard', { defaultValue: 'Standard' })}: {service.processStandard},{' '}
                          {t('Scope of work', { defaultValue: 'Scope of work' })}:{' '}
                          {service.scopeOfWork || 'N/A'},{' '}
                          {t('Scope confirmed', { defaultValue: 'Scope confirmed' })}:{' '}
                          {service.scopeConfirmed || 'N/A'}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          );
        }
        return <> </>;
      },
    },
    {
      field: 'teamMembers',
      display: 'flex',
      headerName: t('Team members', { defaultValue: 'Team members' }).toString(),
      flex: 1.5,
      minWidth: 200,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (params.row.teamMembers) {
          const title = params.row.teamMembers
            .map((member) => `${member.name} ${member.role}`)
            .join(',\n');
          return (
            <ul
              style={{ paddingLeft: 15 }}
              className="MuiDataGrid-cellContent"
              title={title || ' '}
            >
              {params.row.teamMembers.map((member) => (
                <li key={member.id}>
                  {member.name} {member.role}
                </li>
              ))}
            </ul>
          );
        }
        return <> </>;
      },
    },
    {
      field: 'status',
      display: 'flex',
      headerName: t('Status', { defaultValue: 'Status' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
    },
    {
      field: 'numOfEmployees',
      display: 'flex',
      headerName: t('Number of employees', { defaultValue: 'Number of employees' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: 'isMultisite',
      display: 'flex',
      headerName: t('Is multisite', { defaultValue: 'Is multisite' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
    },
    {
      field: 'datStart',
      display: 'flex',
      headerName: t('Start date', { defaultValue: 'Start date' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      valueGetter(_, row) {
        return row.datStart ? getBeautifiedLocalDate(row.datStart) : 'N/A';
      },
    },
    {
      field: 'datEnd',
      display: 'flex',
      headerName: t('End date', { defaultValue: 'End date' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      valueGetter(_, row) {
        return row.datEnd ? getBeautifiedLocalDate(row.datEnd) : 'N/A';
      },
    },
  ];

  return (
    <BrowseView
      browserViewContainerSx={{
        height: '70vh',
      }}
      columns={columns}
      fetchData={(props) => getAuditorVisitsForMeOpened({ ...props, lang })}
      toolbar={<BrowseView.Toolbar showSearch />}
      getRowHeight={() => 'auto'}
    />
  );
};

export default OpenedBrowseView;
